import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { 
  ArrowLeft, Users, UserPlus, Trophy, Target, Flame,
  Bell, Check, X, Send, Crown, Medal
} from "lucide-react";
import { toast } from "sonner";
import { devError } from "../utils/devLogger";

const Challenges = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [tab, setTab] = useState('challenges');
  const [challenges, setChallenges] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [partner, setPartner] = useState(null);
  const [invites, setInvites] = useState({ sent: [], received: [] });
  const [partnerEmail, setPartnerEmail] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [challengesRes, partnerRes, invitesRes, progressRes] = await Promise.all([
        axios.get(`${API}/challenges`),
        axios.get(`${API}/partner`),
        axios.get(`${API}/partner/invites`),
        axios.get(`${API}/challenges/progress`)
      ]);
      setChallenges(challengesRes.data.challenges);
      setLeaderboard(challengesRes.data.leaderboard);
      setPartner(partnerRes.data.partner);
      setInvites(invitesRes.data);
    } catch (error) {
      devError("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const joinChallenge = async (challengeId) => {
    try {
      await axios.post(`${API}/challenges/${challengeId}/join`);
      toast.success("Challenge joined!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join challenge");
    }
  };

  const invitePartner = async () => {
    if (!partnerEmail.trim()) return;
    try {
      await axios.post(`${API}/partner/invite`, { partner_email: partnerEmail });
      toast.success(t('challenges.invitationSent'));
      setPartnerEmail('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    }
  };

  const respondToInvite = async (inviteId, accept) => {
    try {
      await axios.post(`${API}/partner/respond`, { invite_id: inviteId, accept });
      toast.success(accept ? t('challenges.partnershipCreated') : "Invitation declined");
      fetchData();
    } catch (error) {
      toast.error("Failed to respond to invitation");
    }
  };

  const nudgePartner = async () => {
    try {
      await axios.post(`${API}/partner/nudge`);
      toast.success(t('challenges.nudgeSent'));
    } catch (error) {
      toast.error("Failed to send nudge");
    }
  };

  const getChallengeIcon = (type) => {
    switch(type) {
      case 'physical': return Flame;
      case 'cognitive': return Trophy;
      case 'mindfulness': return Users;
      case 'balance': return Target;
      default: return Trophy;
    }
  };

  const getChallengeColor = (type) => {
    switch(type) {
      case 'physical': return 'bg-cyan-100 text-cyan-600';
      case 'cognitive': return 'bg-indigo-100 text-indigo-600';
      case 'mindfulness': return 'bg-pink-100 text-pink-600';
      case 'balance': return 'bg-amber-100 text-amber-600';
      default: return 'bg-sky-100 text-sky-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sky-50 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-sky-400/30 glow-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sky-50" data-testid="challenges-page">
      <div className="fixed top-0 right-0 w-96 h-96 bg-amber-300/10 rounded-full blur-3xl pointer-events-none" />
      
      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-sky-600 hover:text-sky-800"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
              {t('common.dashboard')}
            </Button>
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" strokeWidth={1.5} />
              <span className="text-sm text-sky-700">{t('challenges.title')}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setTab('challenges')}
              className={`px-4 py-2 rounded-full transition-all ${
                tab === 'challenges' ? 'bg-sky-400 text-white' : 'bg-white/60 text-sky-700'
              }`}
            >
              <Trophy className="w-4 h-4 inline mr-2" />
              {t('challenges.challenges')}
            </button>
            <button
              onClick={() => setTab('partner')}
              className={`px-4 py-2 rounded-full transition-all ${
                tab === 'partner' ? 'bg-sky-400 text-white' : 'bg-white/60 text-sky-700'
              }`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              {t('challenges.partner')}
            </button>
          </div>

          {tab === 'challenges' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                <h2 className="text-xl font-light text-sky-900" style={{ fontFamily: 'Outfit' }}>
                  {t('challenges.activeChallenges')}
                </h2>
                {challenges.map((challenge) => {
                  const Icon = getChallengeIcon(challenge.type);
                  const progressPct = (challenge.progress / challenge.requirement.count) * 100;
                  
                  return (
                    <div
                      key={challenge.id}
                      className={`glass-card p-5 ${challenge.status === 'completed' ? 'border-2 border-green-300' : ''}`}
                      data-testid={`challenge-${challenge.id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-xl ${getChallengeColor(challenge.type)} flex items-center justify-center`}>
                          <Icon className="w-6 h-6" strokeWidth={1.5} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-sky-800">{challenge.name}</h3>
                            {challenge.status === 'completed' && (
                              <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded-full">
                                {t('challenges.completed')}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 mt-1">{challenge.description}</p>
                          
                          {challenge.status === 'active' && (
                            <div className="mt-3">
                              <div className="flex justify-between text-xs text-slate-500 mb-1">
                                <span>Progress</span>
                                <span>{challenge.progress}/{challenge.requirement.count}</span>
                              </div>
                              <Progress value={progressPct} className="h-2" />
                            </div>
                          )}
                          
                          <div className="flex items-center gap-4 mt-3">
                            <span className="text-xs text-slate-400">{challenge.duration_days} {t('common.days')}</span>
                            <span className="text-xs text-amber-500">{challenge.reward_points} {t('common.points')}</span>
                          </div>
                        </div>
                        
                        {challenge.status === 'available' && (
                          <Button
                            size="sm"
                            className="glow-button"
                            onClick={() => joinChallenge(challenge.id)}
                          >
                            {t('challenges.join')}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="glass-card p-5" data-testid="leaderboard">
                <h3 className="text-lg font-medium text-sky-800 mb-4 flex items-center gap-2">
                  <Crown className="w-5 h-5 text-amber-500" />
                  {t('challenges.leaderboard')}
                </h3>
                {leaderboard.length > 0 ? (
                  <div className="space-y-3">
                    {leaderboard.map((entry, idx) => (
                      <div key={entry.user_id} className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                          ${idx === 0 ? 'bg-amber-400 text-white' : ''}
                          ${idx === 1 ? 'bg-slate-300 text-white' : ''}
                          ${idx === 2 ? 'bg-amber-600 text-white' : ''}
                          ${idx > 2 ? 'bg-sky-100 text-sky-600' : ''}
                        `}>
                          {idx + 1}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-sky-800">{entry.name}</p>
                          <p className="text-xs text-slate-500">{entry.challenges_completed} {t('challenges.challenges').toLowerCase()}</p>
                        </div>
                        <span className="text-amber-500 font-medium">{entry.total_points}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">
                    {t('challenges.noCompletions')}
                  </p>
                )}
              </div>
            </div>
          )}

          {tab === 'partner' && (
            <div className="max-w-2xl mx-auto">
              {partner ? (
                <div className="glass-card p-6" data-testid="partner-card">
                  <div className="text-center mb-6">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-sky-400 to-indigo-400 flex items-center justify-center">
                      {partner.picture ? (
                        <img src={partner.picture} alt="" className="w-full h-full rounded-full" />
                      ) : (
                        <span className="text-3xl text-white">{partner.name[0]}</span>
                      )}
                    </div>
                    <h3 className="text-xl font-medium text-sky-800">{partner.name}</h3>
                    <p className="text-sm text-slate-500">{t('challenges.accountabilityPartner')}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="text-center p-4 bg-white/50 rounded-xl">
                      <p className="text-2xl font-light text-amber-500" style={{ fontFamily: 'Outfit' }}>
                        {partner.current_streak}
                      </p>
                      <p className="text-xs text-slate-500">{t('challenges.dayStreak')}</p>
                    </div>
                    <div className="text-center p-4 bg-white/50 rounded-xl">
                      <p className="text-2xl font-light text-sky-500" style={{ fontFamily: 'Outfit' }}>
                        {partner.exercises_today}
                      </p>
                      <p className="text-xs text-slate-500">{t('challenges.todaysExercises')}</p>
                    </div>
                  </div>
                  
                  <div className={`p-4 rounded-xl text-center mb-6 ${partner.active_today ? 'bg-green-50' : 'bg-amber-50'}`}>
                    {partner.active_today ? (
                      <p className="text-green-600 flex items-center justify-center gap-2">
                        <Check className="w-5 h-5" />
                        {t('challenges.activeToday')}
                      </p>
                    ) : (
                      <p className="text-amber-600">
                        {t('challenges.notActiveToday')}
                      </p>
                    )}
                  </div>
                  
                  {!partner.active_today && (
                    <Button onClick={nudgePartner} className="w-full glow-button">
                      <Bell className="w-5 h-5 mr-2" />
                      {t('challenges.sendNudge')}
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  {invites.received.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-medium text-sky-800 mb-3">{t('challenges.pendingInvitations')}</h3>
                      {invites.received.map((invite) => (
                        <div key={invite.id} className="glass-card p-4 flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center">
                            <UserPlus className="w-5 h-5 text-sky-500" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-sky-800">{invite.user_name}</p>
                            <p className="text-sm text-slate-500">{t('challenges.wantsToBePartner')}</p>
                          </div>
                          <Button size="sm" onClick={() => respondToInvite(invite.id, true)} className="glow-button">
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => respondToInvite(invite.id, false)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="glass-card p-6" data-testid="invite-form">
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-sky-100 flex items-center justify-center">
                        <Users className="w-8 h-8 text-sky-400" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-xl font-medium text-sky-800">{t('challenges.findPartner')}</h3>
                      <p className="text-sm text-slate-500 mt-2">
                        {t('challenges.partnerDescription')}
                      </p>
                    </div>
                    
                    <div className="flex gap-3">
                      <Input
                        type="email"
                        placeholder={t('challenges.partnerEmail')}
                        value={partnerEmail}
                        onChange={(e) => setPartnerEmail(e.target.value)}
                        className="flex-1"
                      />
                      <Button onClick={invitePartner} className="glow-button">
                        <Send className="w-5 h-5" />
                      </Button>
                    </div>
                    
                    {invites.sent.length > 0 && (
                      <div className="mt-4 text-sm text-slate-500">
                        <p>Pending invites sent to:</p>
                        {invites.sent.map((invite) => (
                          <p key={invite.id} className="text-sky-600">{invite.partner_email}</p>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Challenges;
